import accounting from "accounting";
import { nullish } from 'Lib/type-sanity';
import moment from 'moment-timezone';

/**
 * Format a date to a format of choice using momentjs syntax.
 * @param { string } value
 * @param { string } fmt_to
 * @param { string } fmt_from
 */

// eslint-disable-next-line snakecasejs/snakecasejs
// @deprecated: This function is over-engineered and doesn't work well with moment
export function formatDate(value, fmt_to = "YYYY-MM-DD", fmt_from = "YYYY-MM-DD H:m:s", local = false) {
  const this_moment = moment(moment(value).format(fmt_from));
  if (local === true) {
    this_moment.local();
  }
  return nullish(value) ? '' : this_moment.format(fmt_to);
}

/**
 * Returns the input `date_str` in a given format (defaults to `YYYY-MM-DD HH:mm:ss` UTC).
 *
 * @param { string } date_str
 * @param { string } [input_format]
 *
 * @returns { string }
 */
// todooo: update this to formatDate and delete old one above
export const formatDateNew = (date_str, input_format) => {
  const m = (input_format && typeof input_format === 'string') ? moment(date_str, input_format) : moment(date_str);
  if (window.user?.timezone) m.tz(window.user.timezone);
  return m.format("YYYY-MM-DD");
};

/**
 * Returns the input `date_str` in a given format (defaults to `YYYY-MM-DD HH:mm:ss` UTC).
 *
 * @param { string } date_str
 * @param { string } [input_format]
 *
 * @returns { string }
 */
export const formatDateTime = (date_str, input_format, output_format = "YYYY-MM-DD HH:mm:ss") => {
  const m = (input_format && typeof input_format === 'string') ? moment(date_str, input_format) : moment(date_str);
  if (window.user?.timezone) m.tz(window.user.timezone);
  return m.format(output_format);
};

/**
 * Convert bytes into human readable format.
 *
 * @param {number} bytes
 * @param {number} decimals
 *
 * @returns {string}
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export function formatBytes(bytes, decimals) {
  if (typeof bytes !== "number") {
    console.error(`Invalid type: got ${typeof bytes}, expected number`);
  }
  if (decimals !== undefined && typeof decimals !== `number`) {
    console.error(`Invalid type: got ${typeof decimals}, expected number`);
  }

  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1000,
    dm = decimals || 2,
    sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format the input byte count according to the supplied unit, optionally round to
 * the amount of decimals indicated by the third parameter.
 * Kilo, Mega, Giga -> 1000, 1000 ^ 2, 1000 ^ 3...
 * Kibi, Mebi, Gibi -> 1024, 1024 ^ 2, 1024 ^ 3...
 *
 * @param {number} bytes
 * @param {string} unit
 * @param {number | undefined} decimals
 *
 * @returns {string}
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export function formatBytesAs(bytes, unit, decimals) {
  const valid_type = (val, ...types) => {
    if (!types.some(type => typeof val === type)) console.error(`Invalid type: expected ${types.reduce((acc, type) => acc += type, ``)}, got ${typeof val}`);
  };
  valid_type(bytes, `number`);
  valid_type(unit, `string`);
  valid_type(decimals, `number`, `undefined`);
  if (decimals === undefined) decimals = 2;

  if (bytes === 0) {
    return `0 Bytes`;
  } else {
    const divisor = (() => {
      switch (unit.toLowerCase()) {
        case `b`:
        case `byte`:
          return 1;
        case `kb`:
        case `kilobyte`:
          return 1000;
        case `kib`:
        case `kibibyte`:
          return 1024;
        case `mb`:
        case `megabyte`:
          return Math.pow(1000, 2);
        case `mib`:
        case `mebibyte`:
          return Math.pow(1024, 2);
        case `gb`:
        case `gigabyte`:
          return Math.pow(1000, 3);
        case `gib`:
        case `gibibyte`:
          return Math.pow(1024, 3);
        case `tb`:
        case `terabyte`:
          return Math.pow(1000, 4);
        case `tib`:
        case `tibibyte`:
          return Math.pow(1024, 4);
      }
    })();
    return `${(bytes / divisor).toFixed(decimals)} ${unit}`;
  }
}

/**
 * Format Number to 2 decimals (by default)
 * @param {*} value
 * @param {*} decimals
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export function formatNumber(value, decimals = 2) {
  return accounting.formatNumber(value, decimals);
}

/**
 * Highlight certain action labels dependent on their type
 * @param value
 * @returns {*}
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export function actionLabel(value) {
  const lower_value = value.toLowerCase();
  const delete_words = ["destroy", "delete"];
  const update_words = ["update", "upload", "modify"];
  const create_words = ["create", "add", "store", "convert", "reduce", "save"];

  for (let i = 0; i < delete_words.length; i++) {
    if (lower_value.includes(delete_words[i])) {
      return `<span class="kt-badge kt-badge--danger kt-badge--wide">${value}</span>`;
    }
  }

  for (let i = 0; i < update_words.length; i++) {
    if (lower_value.includes(update_words[i])) {
      return `<span class="kt-badge kt-badge--info kt-badge--wide">${value}</span>`;
    }
  }

  for (let i = 0; i < create_words.length; i++) {
    if (lower_value.includes(create_words[i])) {
      return `<span class="kt-badge kt-badge--success kt-badge--wide">${value}</span>`;
    }
  }
  return value;
}

/**
 * @param {string} value - Value in seconds
 * @returns {string} - HH:mm:ss
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export function convertToHhmmss(value) {
  value = parseFloat(value);
  const duration = moment.duration(value, "seconds");
  let seconds = duration._data.seconds;
  let minutes = duration._data.minutes;
  let hours = duration._data.hours;

  if (seconds < 10) {
    seconds = "0" + seconds;
  }
  if (minutes < 10) {
    minutes = "0" + minutes;
  }
  if (hours < 10) {
    hours = "0" + hours;
  }

  return hours + ":" + minutes + ":" + seconds;
}

/**
 * Take a UTC datetime from the database and convert it to user local time
 * @param {string} value - Mysql DateTime from database
 * @param {string} format - Output Format
 * @returns {string}
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export function mysqlDateTimeUTCToLocal(value, format = "YYYY-MM-DD HH:mm:ss") {
  return moment.utc(value, 'YYYY-MM-DD HH:mm:ss').tz(window.user.timezone).format(format);
}

/**
 *
 * @param {{ days: number, hours: number, minutes: number, seconds: number }} obj
 *
 * @returns { number }
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export const durationObjToUnit = (obj, unit = `seconds`) => {
  const as_seconds = Object.entries(obj).reduce((acc, [k, v]) => (() => {
    switch (k) {
      case `days`:
        return acc + v * (60 * 60 * 24);
      case `hours`:
        return acc + v * (60 * 60);
      case `minutes`:
        return acc + v * 60;
      case `seconds`:
      default:
        return acc + v;
    }
  })(), 0);
  return (() => {
    switch (unit) {
      case `days`:
        return as_seconds / 60 / 60 / 24;
      case `hours`:
        return as_seconds / 60 / 60;
      case `minutes`:
        return as_seconds / 60;
      case `seconds`:
      default:
        return as_seconds;
    }
  })();
};

/**
 * Capitalise the first letter of each of a strings words.
 *
 * @param { string } str
 * @param { boolean } first_only Whether or not to only capitalise the first word of `str`.
 *
 * @returns { string }
 *
 */
export const capitaliseStr = (str, first_only = false) => {
  if (first_only) {
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
  } else {
    return str.replace(/(?:\b)(\w+)/gmi, (...args) => {
      const word = args[1];
      return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    });
  }
};
